/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useState, useMemo, useCallback } from 'react';
import { defaultStoreUS } from '@/constants/locale';
import { useSignInFlowType } from '@/components/SignInFlowTypeContext';
import dynamic from 'next/dynamic';
import ProgressModal from '@/components/AZCustomComponent/ProgressModal';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import { useAppData } from '@/hooks/useAppData';
import React from 'react';

const LazySignInModalV2 = dynamic(() => import('@/components/SignInModalV2'), {
  loading: () => <ProgressModal noScroll />,
});
const LazySearchStoreModal = dynamic(
  () => import('@/components/Store/StoreModal/SearchStoreModal').then((m) => m.SearchStoreModal),
  { loading: () => <ProgressModal noScroll /> },
);
const LazyMyStoreModal = dynamic(
  () => import('@/components/Store/StoreModal/MyStoreModal').then((m) => m.MyStoreModal),
  { loading: () => <ProgressModal noScroll /> },
);

const MODAL_LINKS = [
  '[cookie_preference_center_modal]',
  '[search_store_modal]',
  '[sign_in_modal]',
  '[create_account_modal]',
];

export function useCMSRichTextModal(htmlContent?: string) {
  const { setFlowTypeFn } = useSignInFlowType();
  const { appData } = useAppData();
  const isLoggedIn = appData.userAuthenticated;
  const { data: storeData } = useStoreDetailsData();
  const storeNumber = storeData?.storeNumber ?? defaultStoreUS;

  const [storeModalOpen, setStoreModalOpen] = useState(false);
  const [signInModalOpen, setSignInModalOpen] = useState(false);

  const toggleStoreModal = useCallback(() => setStoreModalOpen((s) => !s), []);

  const handleModalOpen = useCallback(
    (id: string) => {
      switch (id) {
        case '[search_store_modal]':
          toggleStoreModal();
          break;
        case '[sign_in_modal]':
        case '[create_account_modal]':
          if (isLoggedIn || signInModalOpen) {
            return;
          }
          setFlowTypeFn?.('login');
          setSignInModalOpen(true);
          break;
        case '[cookie_preference_center_modal]':
          window.OneTrust?.ToggleInfoDisplay();
          break;
      }
    },
    [isLoggedIn, signInModalOpen, setFlowTypeFn, toggleStoreModal],
  );

  const processedHTML = useMemo(() => {
    if (!htmlContent) {
      return '';
    }
    let richTextContent = htmlContent;

    MODAL_LINKS.forEach((modalId) => {
      const escapedModalId = modalId.replace(/[[\]]/g, '\\$&');
      const regex = new RegExp(`<a([^>]*?)href="${escapedModalId}"([^>]*?)>([^<]*?)</a>`, 'gi');
      richTextContent = richTextContent.replace(
        regex,
        `<span$1 data-modal="${modalId}" tabindex="0" aria-haspopup="dialog" style="text-decoration: underline; cursor: pointer;"$2>$3</span>`,
      );
    });

    return richTextContent;
  }, [htmlContent]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const el = (event.target as HTMLElement).closest('[data-modal]');
      const modalId = el?.getAttribute('data-modal');
      if (modalId) {
        event.preventDefault();
        event.stopPropagation();
        handleModalOpen(modalId);
      }
    },
    [handleModalOpen],
  );

  const StoreModal = storeNumber !== defaultStoreUS ? LazyMyStoreModal : LazySearchStoreModal;

  const ModalComponents = useCallback(
    () => (
      <>
        {signInModalOpen && (
          <LazySignInModalV2 isOpen={signInModalOpen} onClose={() => setSignInModalOpen(false)} />
        )}
        {storeModalOpen && <StoreModal closeStoreModal={toggleStoreModal} />}
      </>
    ),
    [signInModalOpen, storeModalOpen, StoreModal, toggleStoreModal],
  );

  return { processedHTML, handleClick, ModalComponents, handleModalOpen };
}
